import { IconX } from "@tabler/icons-react";
import RoleToggle from "../../../components/RoleToggle";
import { useEffect, useState } from "react";
import NewinputField from "../../../components/NewInput";
import { FieldError, useForm } from "react-hook-form";
import MultiSelectInputCheckbox from "../../../components/MultiSelectInputCheckbox";
import {
  useFetchNotificationDetailsData,
  useNotificationUpdateMutation,
} from "../../../apis/notificationApis";
import formatDate from "../../../utilities/helper";
import toast from "react-hot-toast";
import NewSelectInput from "../../../components/NewSelectInput";
import { useFetchStaffsAllData } from "../../../apis/staffApis";
import { useMerchantAllListData } from "../../../apis/merchantApis";
import { useFetchDriversAllData } from "../../../apis/driverApis";

type ViewNotificationModalProps = {
  open: boolean;
  onClose: () => void;
  notification?: any;
  refetch: () => void;
};

const roles = ["Details", "History"];

const ViewNotificationModal = ({
  open,
  onClose,
  notification,
  refetch,
}: ViewNotificationModalProps) => {
  const [activeRole, setActiveRole] = useState("Details");
  const [editMode, setEditMode] = useState(false);

  const { data } = useFetchNotificationDetailsData(notification?.id);
  const { mutate, isPending } = useNotificationUpdateMutation(notification?.id);

  const {
    register,
    clearErrors,
    setValue,
    watch,
    reset,
    formState: { errors },
    handleSubmit,
  } = useForm<any>({
    defaultValues: {
      recipients: [],
      date: "",
      time: "",
      selectFor: "",
    },
  });

  // Prefill data when API response comes
  useEffect(() => {
    if (data) {
      reset({
        recipients: data?.recipients || [],
        selectFor: data?.for,
        date: data.scheduleDate
          ? new Date(data.scheduleDate).toISOString().split("T")[0]
          : new Date(data.created_at).toISOString().split("T")[0],
        time: data.scheduleTime
          ? data.scheduleTime
          : new Date(data.created_at).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }),
      });
    }
  }, [data, reset]);

  const { data: staffData } = useFetchStaffsAllData();
  const { data: clients } = useMerchantAllListData();
  const { data: driverData } = useFetchDriversAllData("");
  const selectFor = watch("selectFor");

  // Map each type to its data and how to extract the name
  const recipientMap: Record<
    string,
    { data: any[]; getText: (item: any) => string }
  > = {
    Staff: { data: staffData || [], getText: (s: any) => s?.name },
    Client: { data: clients || [], getText: (c: any) => c?.name },
    Driver: { data: driverData || [], getText: (d: any) => d?.name },
  };

  // Determine which types to include
  const typesToInclude =
    selectFor === "All" ? Object.keys(recipientMap) : [selectFor];

  // Build recipientsData
  const recipientsData = watch("selectFor")
    ? typesToInclude?.flatMap((type) =>
        recipientMap[type]?.data.map((item) => ({
          id: item?.id,
          value: item?.id,
          text: recipientMap[type].getText(item),
          type,
        }))
      )
    : [];

  type LogItemProps = { module: string; time: string; desc: string };
  const LogItem = ({ module, time, desc }: LogItemProps) => (
    <div className="flex items-center gap-3 bg-[#FCFDF8] border border-[#D2D2D2] rounded-xl p-4">
      <img
        src={data?.image || "/img/notification-img.png"}
        alt=""
        className="w-20 h-16 rounded flex-shrink-0 object-cover"
      />
      <div className="space-y-1">
        <div className="text-sm text-[#212121]/70 flex items-center space-x-2">
          <span className="border border-[#D2D2D2] rounded-lg px-2 py-0.5">
            {module}
          </span>
          <span>{time}</span>
        </div>
        <p className="text-base text-[#212121]">{desc}</p>
      </div>
    </div>
  );

  if (!open) return null;

  const onSubmit = (data: any) => {
    if (!editMode) {
      setEditMode(true);
      return;
    }

    const payload = {
      scheduleDate: new Date(data?.date),
      scheduleTime: data?.time,
      recipients: data?.recipients.map((r: any) => ({
        id: r.id,
        type: r.type,
      })),
      for: data?.selectFor,
    };
    mutate(payload, {
      onSuccess: (res: any) => {
        clearErrors();
        reset();
        onClose();
        refetch();
        setEditMode(false);
        setActiveRole("Details");
        toast.success(res?.message || "Notification Updated Successfully");
      },
      onError: (error: any) => {
        console.error(error);
        toast.error(error?.response?.data?.message || "Something went wrong");
      },
    });
  };

  const handleClose = () => {
    setEditMode(false);
    setActiveRole("Details");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-[#F7F6F2] rounded-3xl lg:w-[800px] w-11/12 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-medium text-[#212121]">View Details</h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-full border border-[#D2D2D2]"
          >
            <IconX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="px-4 py-3 space-y-4">
            <div className="flex justify-center items-center">
              <RoleToggle
                options={roles}
                active={activeRole}
                onChange={setActiveRole}
              />
            </div>

            {activeRole === "Details" && (
              <div className="space-y-4">
                {/* Notification Preview */}
                <div className="bg-white p-4 border rounded-[10px] border-[#D2D2D2] space-y-2 w-96 mx-auto">
                  <h2 className="text-[#212121] text-base">
                    Notification Preview
                  </h2>
                  <img
                    src={data?.image || "/img/notification-img.png"}
                    className="h-60 w-full object-cover rounded-lg"
                    alt=""
                  />
                  <h2 className="text-[#212121] text-sm font-semibold">
                    {data?.title}
                  </h2>
                  <p className="text-[#212121]/70 text-sm">
                    {(data?.message || "").split(/(https?:\/\/[^\s]+)/g).map((part: string, i: number) =>
                      /^https?:\/\//.test(part) ? (
                        <a
                          key={i}
                          href={part}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="text-blue-600 underline break-all hover:text-blue-800"
                        >
                          Download Error Sheet
                        </a>
                      ) : (
                        <span key={i}>{part}</span>
                      )
                    )}
                  </p>
                </div>

                {/* Form Inputs */}
                <div className="grid grid-cols-4 gap-4">
                  <NewSelectInput
                    label="Select For"
                    className="col-span-1"
                    name="selectFor"
                    selectOptions={
                      ["All", "Client", "Staff", "Driver"]?.map((r: any) => ({
                        id: r,
                        value: r,
                        text: r,
                      })) || []
                    }
                    register={register}
                    clearErrors={clearErrors}
                    validation={{ required: "Select For is required" }}
                    error={errors.selectFor}
                    value={watch("selectFor")}
                    setValue={(val: string) => {
                      setValue("selectFor", val);
                    }}
                    disabled={!editMode}
                  />
                  <MultiSelectInputCheckbox
                    label="Recipients"
                    name="recipients"
                    selectOptions={recipientsData}
                    register={register}
                    clearErrors={clearErrors}
                    validation={{ required: "Recipients is required" }}
                    error={errors.recipients as FieldError | undefined}
                    value={watch("recipients")}
                    setValue={(val: any) =>
                      setValue("recipients", val, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                    multiple
                    disabled={!editMode}
                  />

                  <NewinputField
                    type="date"
                    name="date"
                    title="Date"
                    register={register}
                    value={watch("date")}
                    error={errors.date}
                    disabled={!editMode}
                  />

                  <NewinputField
                    type="time"
                    name="time"
                    title="Time"
                    register={register}
                    value={watch("time")}
                    error={errors.time}
                    disabled={!editMode}
                  />
                </div>
              </div>
            )}

            {activeRole === "History" && (
              <div className="space-y-8 pr-2 max-h-[500px] overflow-hidden overflow-y-auto">
                <div className="space-y-4">
                  {data?.recipients?.map((log: any, idx: number) => (
                    <LogItem
                      key={idx}
                      module={log?.type}
                      desc={data?.title}
                      time={data?.scheduleTime || formatDate(data?.created_at)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {activeRole === "Details" && !data?.sent && (
            <div className="border-t border-[#D8D8D8] px-4 py-4 flex justify-end space-x-2">
              <button
                type="submit"
                className="px-6 py-2 bg-[#FBD037]/20 border border-[#FBD037] text-[#212121] font-medium rounded-full"
              >
                {!editMode ? "Edit" : isPending ? "Updating..." : "Update"}
              </button>

              <button
                type="button"
                className="px-6 py-2 bg-[#FBD037] text-[#212121] font-medium rounded-full"
              >
                Send
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ViewNotificationModal;
